import * as React from "react";
import {
	Alert,
	Box,
	Button,
	Card,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Grid,
	Link,
	Stack,
	Typography,
} from "@mui/material";
import { ArrowLeftIcon } from "@phosphor-icons/react/dist/ssr/ArrowLeft";
import { useBeforeUnload, useBlocker, useNavigate, useSearchParams } from "react-router-dom";

import type { CampaignRequestDetail } from "@/hooks/api/use-campaign-requests";
import { useCampaignRequest } from "@/hooks/api/use-campaign-requests";
import { Breadcrumbs } from "@/components/breadcrumbs/breadcrumbs";
import { RouterLink } from "@/components/core/link";

import { isFrozen, REQUEST_STATE_CLIENT_LABELS } from "../constants";
import { completedSteps, progressOfRequest } from "../progress";
import { StepAgents } from "./step-agents";
import { StepBasics } from "./step-basics";
import { StepCompliance } from "./step-compliance";
import { StepLeads } from "./step-leads";
import { StepMarkets } from "./step-markets";
import { StepOutcomes } from "./step-outcomes";
import { StepQuote } from "./step-quote";
import { StepReview } from "./step-review";
import { StepStart } from "./step-start";
import { parseStep, REQUESTS_PATH, wizardPath } from "./steps";
import type { StepProps } from "./wizard-shell";
import { stepCounter, WizardDirtyProvider, WizardRail } from "./wizard-shell";

/**
 * The nine-step wizard.
 *
 * Two things about it are load-bearing. The request is persisted from step 0 — the draft exists
 * before the client ever sees step 1 — so every later step has something to write against and
 * nothing is held in a browser waiting to be lost. And the whole position lives in the URL:
 * `?request=<id>&step=<n>` is the entire state, so a refresh, a bookmark or a link pasted into a
 * message resumes exactly where it left off.
 *
 * Each step saves through its own endpoint before it advances, which is why the footer belongs to
 * the step rather than to this shell.
 */

/** The step components, by index. Index 0 is handled separately: it has no request yet. */
const STEP_COMPONENTS: (React.ComponentType<StepProps> | null)[] = [
	null,
	StepBasics,
	StepAgents,
	StepMarkets,
	StepLeads,
	StepOutcomes,
	StepCompliance,
	StepQuote,
	StepReview,
];

export function CreateCampaignRequest(): React.JSX.Element {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const requestId = searchParams.get("request");
	const step = parseStep(searchParams.get("step"));

	const { data: request, isLoading, isError } = useCampaignRequest(requestId);

	/**
	 * Settled, so every step below is a reader rather than an editor.
	 *
	 * The banner underneath used to be the whole of it: the fields stayed live, the client
	 * retyped a market or a window, and Continue answered with a 422 from an endpoint that was
	 * never going to accept it. Nothing that cannot be saved is offered as editable.
	 */
	const frozen = Boolean(request && isFrozen(request.state));

	// A step number without a request behind it cannot be resumed, so the URL is corrected rather
	// than half-honoured — nothing is more confusing than "Step 5 of 8" over an empty screen.
	React.useEffect(() => {
		if (!requestId && step !== 0) navigate(wizardPath(null, 0), { replace: true });
	}, [navigate, requestId, step]);

	const goTo = React.useCallback(
		(next: number) => {
			navigate(wizardPath(requestId, next));
		},
		[navigate, requestId]
	);

	const exit = React.useCallback(() => {
		dirty.current = false;
		navigate(REQUESTS_PATH);
	}, [navigate]);

	const handleStarted = React.useCallback(
		(newRequestId: string) => {
			dirty.current = false;
			navigate(wizardPath(newRequestId, 1));
		},
		[navigate]
	);

	/**
	 * The draft exists — put it in the URL before anything else is attempted against it.
	 *
	 * `replace`, and staying on step 0: this is not the client moving, it is the address catching
	 * up with a row that now exists. Doing it the moment the id is known is what stops a failed
	 * clone from leaving an orphan behind — the retry finds the draft in the URL and repeats into
	 * it instead of writing a second one — and it costs nothing if the rest of step 0 succeeds,
	 * because the very next navigation replaces it with step 1.
	 */
	const handleDraftCreated = React.useCallback(
		(newRequestId: string) => {
			dirty.current = false;
			navigate(wizardPath(newRequestId, 0), { replace: true });
		},
		[navigate]
	);

	/**
	 * The rail is the only navigation in the wizard that saves nothing, so it is the only one
	 * that can throw work away. The step on screen reports whether it is holding any.
	 */
	const dirty = React.useRef(false);
	const reportDirty = React.useCallback((value: boolean) => {
		dirty.current = value;
	}, []);

	const blocker = useBlocker(() => dirty.current);
	useBeforeUnload(
		React.useCallback((event: BeforeUnloadEvent) => {
			if (dirty.current) {
				event.preventDefault();
				event.returnValue = "";
			}
		}, [])
	);
	const selectStep = goTo;
	const discardAndGo = () => {
		dirty.current = false;
		if (blocker.state === "blocked") blocker.proceed();
	};
	const savedGoTo = (next: number) => {
		if (next > step) dirty.current = false;
		goTo(next);
	};

	/**
	 * Which steps are done, from what the request holds rather than from where the cursor is.
	 * Nothing is ticked before there is a request to tick anything about.
	 */
	const completed = React.useMemo(() => (request ? completedSteps(progressOfRequest(request)) : []), [request]);

	const renderStep = (): React.ReactNode => {
		/*
		 * Never over step 0. That step writes the draft and then repeats a campaign into it, and
		 * putting its id in the URL starts the detail query — so a spinner here would unmount the
		 * step half way through its own second write, taking with it the knowledge that the draft
		 * already exists and that the copy is the part still owed. Step 0 renders perfectly well
		 * without the request; every other step is the request.
		 */
		if (isLoading && requestId && step !== 0) {
			return (
				<Card variant="outlined" sx={{ alignItems: "center", display: "flex", justifyContent: "center", p: 6 }}>
					<CircularProgress size={24} />
				</Card>
			);
		}

		if (isError) {
			return (
				<Alert severity="error">
					We could not open that request. It may have been removed, or it may belong to another account.
				</Alert>
			);
		}

		// Step 0 is the only step that runs without a request, because it is the step that creates one.
		if (step === 0 || !request) {
			return (
				<StepStart
					request={request ?? null}
					onDraftCreated={handleDraftCreated}
					onStarted={handleStarted}
					exit={exit}
					readOnly={frozen}
				/>
			);
		}

		const Component = STEP_COMPONENTS[step];
		if (!Component) return null;

		return <Component request={request as CampaignRequestDetail} goTo={savedGoTo} exit={exit} readOnly={frozen} />;
	};

	return (
		<Box
			sx={{
				m: "var(--Content-margin)",
				maxWidth: "var(--Content-maxWidth)",
				p: "var(--Content-padding)",
				width: "var(--Content-width)",
			}}
		>
			<Stack spacing={4}>
				<Stack spacing={3}>
					<Breadcrumbs>
						<Link
							color="text.primary"
							component={RouterLink}
							href={REQUESTS_PATH}
							sx={{ alignItems: "center", display: "inline-flex", gap: 1, width: "max-content" }}
							variant="subtitle2"
						>
							<ArrowLeftIcon fontSize="var(--icon-fontSize-md)" />
							Campaign Requests
						</Link>
						<Typography variant="subtitle2" color="text.secondary" whiteSpace="pre">
							/
						</Typography>
						<Typography variant="subtitle2" color="text.secondary" whiteSpace="pre">
							{request?.title ?? "New Request"}
						</Typography>
					</Breadcrumbs>

					<Stack direction="row" spacing={2} sx={{ alignItems: "baseline", flexWrap: "wrap" }}>
						{/* The campaign's own name once it has one. "New Request" over a breadcrumb
						    already reading "UI review test" is one screen saying two things. */}
						<Typography variant="h4">{request?.title ?? "New Request"}</Typography>
						{stepCounter(step) ? (
							<Typography variant="subtitle1" color="text.secondary">
								{stepCounter(step)}
							</Typography>
						) : null}
					</Stack>
				</Stack>

				{request && isFrozen(request.state) ? (
					<Alert severity="info">
						This request is {REQUEST_STATE_CLIENT_LABELS[request.state].toLowerCase()} — the wizard can no longer change
						it.
					</Alert>
				) : null}

				<Grid container spacing={3}>
					<Grid size={{ xs: 12, md: 3 }}>
						{/* Free navigation only once there is a draft to come back to. */}
						<WizardRail current={step} canNavigate={Boolean(requestId)} completed={completed} onSelect={selectStep} />
					</Grid>

					<Grid size={{ xs: 12, md: 9 }}>
						<WizardDirtyProvider value={reportDirty}>{renderStep()}</WizardDirtyProvider>
					</Grid>
				</Grid>
			</Stack>

			{/*
			 * Asked, not assumed. Jumping the rail is a reasonable thing to want to do mid-edit —
			 * "what did I put on compliance?" — so the choice is offered both ways round, and
			 * staying is the safe default the dialog opens on.
			 */}
			<Dialog open={blocker.state === "blocked"} onClose={() => blocker.state === "blocked" && blocker.reset()}>
				<DialogTitle>Leave this step without saving?</DialogTitle>
				<DialogContent>
					<DialogContentText>
						You have changes on this step that have not been saved. Moving to another step now discards them — use
						Continue or Save &amp; exit to keep them.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button color="inherit" onClick={() => blocker.state === "blocked" && blocker.reset()}>
						Stay on This Step
					</Button>
					<Button color="error" variant="contained" onClick={discardAndGo}>
						Discard Changes
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}
